import { ZodError } from "zod"

import { Base64File } from "@/file-upload/FileUpload.schema"

import { formSchemaMapping, SupportedForm } from "@/lib/form/form.schema"

export const supportedImageExtensions = ['.png', '.heic', '.jpeg', '.jpg']
export const supportedFileExtensions = ['.pdf', '.png', '.heic', '.jpeg', '.jpg']

export const supportedImageMimeTypes = ['image/png', 'image/heic', 'image/jpeg', 'image/jpg']
export const supportedDocumentMimeTypes = ['application/pdf', 'image/png', 'image/heic', 'image/jpeg', 'image/jpg'];

export const damageDiagnosisToCodes = {
    power: '0599',
    network: '0499',
    front: '0701',
    back: '0299',
    audio: '0099',
    camera: '1399'
}

export const codesToDamageDiagnosis = {
    '0599': 'power',
    '0499': 'network',
    '0701': 'front',
    '0299': 'back',
    '0099': 'audio',
    '1399': 'camera'
}

export const stepToDataKey = {
    'claim-type': 'claim_type',
    'when': 'claim_date',
    'damage-diagnosis': 'damage_codes',
    'details': 'additional_info',
    'contact-information': 'insured_person',
    'images': 'uploads.claim_damage_photo',
    'purchase-invoice': 'uploads.claim_damage_photo_invoice',
    'report': 'uploads.claim_policeconfirmation',
}

export const dataKeyToStep = {
    'claim_type': 'claim-type',
    'claim_date': 'when',
    'damage_codes': 'damage-diagnosis',
    'additional_info': 'details',
    'insured_person': 'contact-information',
    'uploads.claim_damage_photo': 'images',
    'uploads.claim_damage_photo_invoice': 'purchase-invoice',
    'uploads.claim_policeconfirmation': 'report',
}

export type FormStateData = Record<string, any>
export type FieldErrors = Record<string, string[]>

export type FormState = {
    data: FormStateData | null
    fieldErrors: FieldErrors | null
}

export type NestedFormState = {
    data: FormStateData | null
    fieldErrors: Record<string, FieldErrors> | null
}

export type FileFieldErrors =
    | { fileExtension: string[] }
    | { emptyFile: string[] }
    | {
          [key in 'imagesAmount' | 'filesAmount' | 'commentsAmount']:
              | string[]
              | { variables: Record<string, string | number> }
      }
    | { unknown: string[] }
    | { bothProvided: string[] }
    | { noneProvided: string[] }
    | null

export type FileUploadFormState = {
    data: Base64File[] | null
    fieldErrors: FileFieldErrors
}

export type CommentUploadFormState = {
    data: Comment[] | null
    fieldErrors: { commentsAmount: string[] } | null
}

export const EMPTY_FORM_STATE = {
    data: null,
    fieldErrors: null,
}

export const validateFormData = (prevState: FormState, formData: FormData, formSchemaName: SupportedForm): FormState => {
    const formDataToObject = Object.fromEntries(formData)

    return validateState({ ...formDataToObject }, formSchemaName)
}

export const validateState = (state: FormStateData, formSchemaName: SupportedForm): FormState => {
    const formSchema = formSchemaMapping[formSchemaName]

    try {
        const validatedData = formSchema.parse(state)

        return {
            data: validatedData,
            fieldErrors: null,
        }
    } catch (error) {
        return fromErrorToFormState(error)
    }
}

export const fromErrorToFormState = (error: unknown): FormState => {
    if (error instanceof ZodError) {
        return {
            data: null,
            fieldErrors: error.flatten().fieldErrors as FormState['fieldErrors'],
        }
    }

    if (error instanceof Error) {
        return {
            data: null,
            fieldErrors: {
                message: [error.message],
            },
        }
    }

    return {
        data: null,
        fieldErrors: {
            // Only the key of the error is important, the value is not used. For more information see the FieldErrors component.
            unknown: ['']
        },
    }
}

export const validateFilesUpload = (data: Base64File[], type: 'file' | 'image' = 'file', min: number = 1, max: number = 5): FileUploadFormState => {
    if (!data || data.length < min || data.length > max) {
        const amountError: any =  { [type === 'image' ? 'imagesAmount' : 'filesAmount']: { variables: { min, max } } }

        return {
            data: null,
            fieldErrors: {
                ...amountError
            }
        }
    }

    const supportedExtensions = type === 'image' ? supportedImageExtensions : supportedFileExtensions
    const supportedMimeTypes = type === 'image' ? supportedImageMimeTypes : supportedDocumentMimeTypes

    const extensionPattern = supportedExtensions.map(ext => ext.replace('.', '')).join('|')
    const fileRegex = new RegExp(`^(.+)\\.(${extensionPattern})$`)

    const hasInvalidFiles = data.some((file: Base64File) => {
        const fileName = file.fname.toLowerCase()
        const mimeType = file.ctype.toLowerCase()

        const hasValidMimeType = supportedMimeTypes.includes(mimeType)
        const hasValidFileName = fileRegex.test(fileName)

        return !hasValidFileName || !hasValidMimeType;
    });

    if (hasInvalidFiles) {
        return {
            data: null,
            fieldErrors: {
                'fileExtension': ['']
            }
        }
    }

    // Verify no empty files.
    const hasEmptyFiles = data.some((file: Base64File) => !file.size || !file.contents)
    if (hasEmptyFiles) {
        return {
            data: null,
            fieldErrors: {
                'emptyFile': ['File is empty, please fill it.']
            }
        }
    }

    return {
        data: data,
        fieldErrors: null
    }
}

type Comment = { key: string; value: string };

export const validateFilesOrCommentsUpload = (files: Base64File[], comments: Comment[], type: 'file' | 'image' = 'file', message: string = ''): FileUploadFormState | CommentUploadFormState => {
    if (files.length === 0 && comments.length === 0 && message.trim().length === 0) {
        return {
            data: null,
            fieldErrors: {
                noneProvided: ['Please provide either files, comments or a message.']
            }
        };
    }

    let responseData: any[] = [];

    const fileUploadLimits = {
        claim_damage_photo: {
            minLimit: 2,
            maxLimit: 5
        },
        claim_damage_photo_other: {
            minLimit: 2,
            maxLimit: 5
        },
        claim_damage_video: {
            minLimit: 1,
            maxLimit: 5
        },
        claim_damage_photo_invoice: {
            minLimit: 1,
            maxLimit: 5
        },
        claim_policeconfirmation: {
            minLimit: 1,
            maxLimit: 3
        },
        claim_lossevaluation: {
            minLimit: 1,
            maxLimit: 5
        },
        claim_repairinvoice: {
            minLimit: 1,
            maxLimit: 5
        },
        claim_claiminfo: {
            minLimit: 1,
            maxLimit: 5
        },
        claim_occuredcostdocument: {
            minLimit: 1,
            maxLimit: 5
        },
        claim_deductibleinvoice: {
            minLimit: 1,
            maxLimit: 5
        },
        claim_other: {
            minLimit: 1,
            maxLimit: 5
        },
        claim_repair_quote: {
            minLimit: 1,
            maxLimit: 5
        }
    };

    if (files.length > 0) {
        responseData = responseData.concat(files);
        const typeMap: { [key: string]: Base64File[] } = {};

        files.forEach(file => {
            if (!typeMap[file.type]) {
                typeMap[file.type] = [];
            }
            typeMap[file.type].push(file);
        });

        for (const type in typeMap) {
            if (Object.prototype.hasOwnProperty.call(typeMap, type)) {

                const validationResponse = validateFilesUpload(
                    typeMap[type], 
                    'file', 
                    fileUploadLimits[type as keyof typeof fileUploadLimits].minLimit, 
                    fileUploadLimits[type as keyof typeof fileUploadLimits].maxLimit
                );

                if (validationResponse.fieldErrors) {
                    return validationResponse;
                }
            }
        }
    } 


    if (comments.length > 0) {
        responseData = responseData.concat(comments);
        const validationResponse = validateCommentsUpload(comments);
        if (validationResponse.fieldErrors) {
            return validationResponse;
        }
    }

    return {
        data: responseData,
        fieldErrors: null
    };
};

// Placeholder for comments validation logic
export const validateCommentsUpload = (comments: Comment[]): CommentUploadFormState => {
    // Implement comments validation logic here
    // For example, check if each comment has both key and value
    const hasInvalidComments = comments.some(comment => !comment.key || !comment.value);

    if (hasInvalidComments) {
        return {
            data: null,
            fieldErrors: {
                'commentsAmount': ['All comments must have both key and value.']
            }
        };
    }

    return {
        data: comments, // Assuming you want to return the comments as data
        fieldErrors: null
    };
};